import { useState, useEffect, useCallback } from 'react'
import { Plus, Edit2, Trash2, X, Users, Power } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { pisteirosService } from '../../services/pisteirosService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function PisteirosPage() {
  const { organizadorAtivo } = useOrganizador()
  const navigate = useNavigate()
  const orgId = organizadorAtivo?.id
  const moduloAtivo = organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe')

  const [pisteiros, setPisteiros] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editando, setEditando] = useState(null)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [mostrarInativos, setMostrarInativos] = useState(false)

  const [form, setForm] = useState({ nome: '', telefone: '', valor_por_lote: '', percentual_venda: '' })
  const [erros, setErros] = useState({})

  useEffect(() => {
    if (!moduloAtivo) {
      navigate('/dashboard', { replace: true })
    }
  }, [moduloAtivo, navigate])

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const res = await pisteirosService.listar(orgId, mostrarInativos)
      setPisteiros(res.data || res || [])
    } catch {
      toast.error('Erro ao carregar pisteiros')
    } finally {
      setLoading(false)
    }
  }, [orgId, mostrarInativos])

  useEffect(() => {
    if (orgId && moduloAtivo) carregar()
  }, [orgId, moduloAtivo, carregar])

  const abrirModal = (pisteiro = null) => {
    if (pisteiro) {
      setEditando(pisteiro)
      setForm({
        nome: pisteiro.nome || '',
        telefone: pisteiro.telefone || '',
        valor_por_lote: pisteiro.valor_por_lote?.toString() || '0',
        percentual_venda: pisteiro.percentual_venda?.toString() || '0'
      })
    } else {
      setEditando(null)
      setForm({ nome: '', telefone: '', valor_por_lote: '', percentual_venda: '' })
    }
    setErros({})
    setShowModal(true)
  }

  const fecharModal = () => {
    setShowModal(false)
    setEditando(null)
    setForm({ nome: '', telefone: '', valor_por_lote: '', percentual_venda: '' })
    setErros({})
  }

  const validar = () => {
    const e = {}
    if (!form.nome.trim()) e.nome = 'Nome é obrigatório'
    const valor = Number(form.valor_por_lote)
    if (form.valor_por_lote === '' || isNaN(valor)) e.valor_por_lote = 'Valor por lote é obrigatório'
    else if (valor < 0) e.valor_por_lote = 'Valor deve ser maior ou igual a zero'
    const pct = Number(form.percentual_venda)
    if (form.percentual_venda !== '' && !isNaN(pct) && (pct < 0 || pct > 100)) e.percentual_venda = 'Percentual deve estar entre 0 e 100'
    setErros(e)
    return Object.keys(e).length === 0
  }

  const salvar = async () => {
    if (!validar()) return
    setSaving(true)
    try {
      const dados = {
        nome: form.nome.trim(),
        telefone: form.telefone.trim() || null,
        valor_por_lote: Number(form.valor_por_lote) || 0,
        percentual_venda: Number(form.percentual_venda) || 0
      }
      if (editando) {
        await pisteirosService.atualizar(orgId, editando.id, dados)
        toast.success('Pisteiro atualizado com sucesso')
      } else {
        await pisteirosService.criar(orgId, dados)
        toast.success('Pisteiro criado com sucesso')
      }
      fecharModal()
      carregar()
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao salvar pisteiro'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const excluir = async (pisteiro) => {
    try {
      await pisteirosService.excluir(orgId, pisteiro.id)
      toast.success('Pisteiro excluído com sucesso')
      setConfirmDelete(null)
      carregar()
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao excluir pisteiro'
      toast.error(msg)
      setConfirmDelete(null)
    }
  }

  const toggleAtivo = async (pisteiro) => {
    try {
      await pisteirosService.toggleAtivo(orgId, pisteiro.id)
      toast.success(pisteiro.ativo ? 'Pisteiro inativado' : 'Pisteiro reativado')
      carregar()
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao alterar status'
      toast.error(msg)
    }
  }

  if (!moduloAtivo) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Pisteiros</h1>
            <p className="text-sm text-gray-500">Gerencie os pisteiros do organizador</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            Mostrar inativos
          </label>
          <button
            onClick={() => abrirModal()}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <Plus size={18} />
            Novo Pisteiro
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : pisteiros.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Nenhum pisteiro cadastrado</p>
            <p className="text-sm mt-1">Clique em "Novo Pisteiro" para começar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Telefone</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Quem Captou (R$)</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Quem Vendeu (%)</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pisteiros.map(p => (
                  <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${!p.ativo ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {p.nome}
                      {!p.ativo && <span className="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">Inativo</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{p.telefone || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmt(p.valor_por_lote)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{Number(p.percentual_venda || 0).toFixed(2)}%</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => toggleAtivo(p)}
                          className={`p-1.5 rounded-lg transition-colors ${p.ativo ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                          title={p.ativo ? 'Inativar' : 'Reativar'}
                        >
                          <Power size={16} />
                        </button>
                        <button
                          onClick={() => abrirModal(p)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(p)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">
                {editando ? 'Editar Pisteiro' : 'Novo Pisteiro'}
              </h2>
              <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${erros.nome ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="Nome do pisteiro"
                />
                {erros.nome && <p className="text-xs text-red-600 mt-1">{erros.nome}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
                <input
                  type="text"
                  value={form.telefone}
                  onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quem Captou — Valor por Lote (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.valor_por_lote}
                  onChange={e => setForm(p => ({ ...p, valor_por_lote: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${erros.valor_por_lote ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-400 mt-1">R$ fixo por cada lote que captou</p>
                {erros.valor_por_lote && <p className="text-xs text-red-600 mt-1">{erros.valor_por_lote}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quem Vendeu — Percentual (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.percentual_venda}
                  onChange={e => setForm(p => ({ ...p, percentual_venda: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${erros.percentual_venda ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-400 mt-1">% em cima da comissão de compra quando vender</p>
                {erros.percentual_venda && <p className="text-xs text-red-600 mt-1">{erros.percentual_venda}</p>}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={fecharModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={salvar}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Salvando...' : editando ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmação Exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-5 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Excluir Pisteiro</h3>
              <p className="text-sm text-gray-600">
                Tem certeza que deseja excluir <strong>{confirmDelete.nome}</strong>?
                {' '}Se houver lotes vinculados, o pisteiro será inativado.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => excluir(confirmDelete)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
